import { useState } from "react";
import { Check, X, MessageCircle, CornerDownRight, MapPin, Clock, FileText, Globe } from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { fmtTime } from "./ReviewAssetViewer";

function timeAgo(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function LocationBadge({ comment }) {
  if (comment.pin_yaw != null) return <Badge variant="outline" className="gap-1 text-[10px]"><Globe className="w-3 h-3" />360° Pin</Badge>;
  if (comment.pin_x != null) return <Badge variant="outline" className="gap-1 text-[10px]"><MapPin className="w-3 h-3" />Pin</Badge>;
  if (comment.timestamp_seconds != null) return <Badge variant="outline" className="gap-1 text-[10px]"><Clock className="w-3 h-3" />{fmtTime(comment.timestamp_seconds)}</Badge>;
  if (comment.page_number != null) return <Badge variant="outline" className="gap-1 text-[10px]"><FileText className="w-3 h-3" />Page {comment.page_number}</Badge>;
  return null;
}

function CommentRow({ comment, isActive, canResolve, onSelect, onResolveToggle, onReply, replies }) {
  const [replying, setReplying] = useState(false);
  const [replyBody, setReplyBody] = useState("");

  return (
    <div
      className={`rounded-lg border p-3 cursor-pointer transition-colors ${isActive ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"} ${comment.resolved ? "opacity-60" : ""}`}
      onClick={() => onSelect?.(comment.id)}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium truncate">{comment.author_name}</span>
          <Badge variant={comment.author_type === "client" ? "secondary" : "default"} className="text-[10px] shrink-0">
            {comment.author_type === "client" ? "Client" : "Staff"}
          </Badge>
        </div>
        <span className="text-[11px] text-muted-foreground shrink-0">{timeAgo(comment.created_at)}</span>
      </div>
      <p className="text-sm mt-1.5 whitespace-pre-wrap break-words">{comment.body}</p>
      <div className="flex items-center gap-2 mt-2">
        <LocationBadge comment={comment} />
        {comment.resolved && <Badge variant="outline" className="text-[10px] gap-1 text-green-600 border-green-300"><Check className="w-3 h-3" />Resolved</Badge>}
        <div className="ml-auto flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-2 text-xs"
            onClick={(e) => { e.stopPropagation(); setReplying((r) => !r); }}
          >
            <CornerDownRight className="w-3 h-3 mr-1" />
            Reply
          </Button>
          {canResolve && (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-2 text-xs"
              onClick={(e) => { e.stopPropagation(); onResolveToggle(comment.id, !comment.resolved); }}
            >
              {comment.resolved ? "Reopen" : "Resolve"}
            </Button>
          )}
        </div>
      </div>

      {replies?.length > 0 && (
        <div className="mt-2 pl-3 border-l-2 border-border space-y-2">
          {replies.map((r) => (
            <div key={r.id}>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium">{r.author_name}</span>
                <Badge variant={r.author_type === "client" ? "secondary" : "default"} className="text-[9px]">
                  {r.author_type === "client" ? "Client" : "Staff"}
                </Badge>
                <span className="text-[10px] text-muted-foreground">{timeAgo(r.created_at)}</span>
              </div>
              <p className="text-xs mt-0.5 whitespace-pre-wrap break-words">{r.body}</p>
            </div>
          ))}
        </div>
      )}

      {replying && (
        <div className="mt-2 flex gap-2" onClick={(e) => e.stopPropagation()}>
          <Textarea
            value={replyBody}
            onChange={(e) => setReplyBody(e.target.value)}
            placeholder="Write a reply…"
            className="text-sm min-h-[36px]"
            rows={1}
          />
          <Button
            size="sm"
            disabled={!replyBody.trim()}
            onClick={() => { onReply(comment.id, replyBody.trim()); setReplyBody(""); setReplying(false); }}
          >
            Send
          </Button>
        </div>
      )}
    </div>
  );
}

// Threaded comment list + composer for a single review asset. Used by both
// the staff workspace and the public client page — canResolve gates the
// resolve/reopen action (both roles can comment, resolve is judgment-call so
// left open to both for now).
export default function ReviewCommentPanel({
  comments,
  activeCommentId,
  pendingContext,
  onSelect,
  onCancelPending,
  onSubmit,
  onResolveToggle,
  canResolve = true,
}) {
  const [body, setBody] = useState("");

  const topLevel = comments.filter((c) => !c.parent_comment_id);
  const repliesFor = (id) => comments.filter((c) => c.parent_comment_id === id);

  const submit = () => {
    if (!body.trim()) return;
    onSubmit({ body: body.trim(), ...(pendingContext || {}) });
    setBody("");
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-1 pb-3 border-b border-border">
        <MessageCircle className="w-4 h-4 text-muted-foreground" />
        <span className="text-sm font-semibold">Comments ({topLevel.length})</span>
      </div>

      <div className="flex-1 overflow-y-auto py-3 space-y-2 min-h-0">
        {topLevel.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8">
            No comments yet. Click on the asset to drop a pin, or add a general note below.
          </p>
        )}
        {topLevel.map((c) => (
          <CommentRow
            key={c.id}
            comment={c}
            replies={repliesFor(c.id)}
            isActive={activeCommentId === c.id}
            canResolve={canResolve}
            onSelect={onSelect}
            onResolveToggle={onResolveToggle}
            onReply={(parentId, replyBody) => onSubmit({ body: replyBody, parent_comment_id: parentId })}
          />
        ))}
      </div>

      <div className="pt-3 border-t border-border">
        {pendingContext && (
          <div className="flex items-center justify-between mb-2 px-2 py-1.5 rounded bg-primary/10 text-xs">
            <span className="flex items-center gap-1 font-medium text-primary">
              {pendingContext.pin_x != null && <><MapPin className="w-3 h-3" />Commenting on dropped pin</>}
              {pendingContext.pin_yaw != null && <><Globe className="w-3 h-3" />Commenting on dropped pin</>}
              {pendingContext.timestamp_seconds != null && <><Clock className="w-3 h-3" />At {fmtTime(pendingContext.timestamp_seconds)}</>}
              {pendingContext.page_number != null && <><FileText className="w-3 h-3" />On page {pendingContext.page_number}</>}
            </span>
            <button onClick={onCancelPending} className="text-muted-foreground hover:text-foreground">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        <div className="flex gap-2">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={pendingContext ? "Describe what you'd like changed…" : "Add a general comment…"}
            className="text-sm min-h-[60px]"
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }}
          />
        </div>
        <div className="flex justify-end mt-2">
          <Button size="sm" disabled={!body.trim()} onClick={submit}>Post Comment</Button>
        </div>
      </div>
    </div>
  );
}
